import CameraAltIcon from '@mui/icons-material/CameraAlt';
import EditIcon from '@mui/icons-material/Edit';
import {
  Avatar,
  Box,
  Button,
  Chip,
  Divider,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import React, { useRef, useState } from 'react';
import { ExperienceLevel } from '../../../../happypaws-shared/src/Experience/experience';
import { useUploadProfilePhoto } from '../../../../hooks/user/useUploadProfilePhoto';

export interface ProfileData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  location: { city: string; state: string; zipCode: string };
  photoUrl: string | null;
  bio: string;
  experience: ExperienceLevel | null;
  languages: string[];
}

interface ProfileSectionProps {
  data: ProfileData;
  onSave: (data: ProfileData) => void;
}

const EXPERIENCE_OPTIONS = [
  { value: ExperienceLevel.BEGINNER, label: 'Less than 1 year' },
  { value: ExperienceLevel.INTERMEDIATE, label: '1–3 years' },
  { value: ExperienceLevel.EXPERIENCED, label: '3–5 years' },
  { value: ExperienceLevel.EXPERT, label: '5–10 years' },
];

const LANGUAGE_OPTIONS = ['English', 'Spanish', 'French', 'German', 'Serbian'];

const ProfileSection: React.FC<ProfileSectionProps> = ({ data, onSave }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<ProfileData>(data);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { mutate: uploadPhoto, isPending: isUploading } = useUploadProfilePhoto();

  const handleEdit = () => {
    setDraft(data);
    setEditing(true);
  };

  const handleCancel = () => {
    setDraft(data);
    setEditing(false);
  };

  const handleSave = () => {
    onSave(draft);
    setEditing(false);
  };

  const handleField = (field: keyof ProfileData, value: string) => {
    setDraft((prev) => ({ ...prev, [field]: value }));
  };

  const handleLocation = (field: keyof ProfileData['location'], value: string) => {
    setDraft((prev) => ({ ...prev, location: { ...prev.location, [field]: value } }));
  };

  const handleLanguageToggle = (lang: string) => {
    setDraft((prev) => ({
      ...prev,
      languages: prev.languages.includes(lang)
        ? prev.languages.filter((l) => l !== lang)
        : [...prev.languages, lang],
    }));
  };

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      alert('File size must be less than 5MB');
      return;
    }

    // Show preview immediately
    const url = URL.createObjectURL(file);
    setDraft((prev) => ({ ...prev, photoUrl: url }));

    // Upload to Supabase
    uploadPhoto(file, {
      onSuccess: (photoUrl) => {
        setDraft((prev) => ({ ...prev, photoUrl }));
      },
    });
  };

  const display = editing ? draft : data;

  return (
    <Paper sx={{ p: 3, borderRadius: 2, mb: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box>
          <Typography variant="h6" fontWeight={600}>
            Profile Information
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Your public profile shown to pet owners
          </Typography>
        </Box>
        {!editing && (
          <Button
            variant="outlined"
            size="small"
            startIcon={<EditIcon />}
            onClick={handleEdit}
            sx={{ textTransform: 'none', borderColor: '#178B8E', color: '#178B8E' }}
          >
            Edit
          </Button>
        )}
      </Box>

      {/* Avatar */}
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
        <Box sx={{ position: 'relative' }}>
          <Avatar
            src={display.photoUrl ?? undefined}
            sx={{
              width: 150,
              height: 150,
              fontSize: '3rem',
              backgroundColor: '#178B8E',
              opacity: isUploading ? 0.7 : 1,
              transition: 'opacity 0.2s',
            }}
          >
            {display.firstName.charAt(0)}
          </Avatar>
          {editing && (
            <>
              <IconButton
                onClick={() => fileInputRef.current?.click()}
                sx={{
                  position: 'absolute',
                  bottom: 4,
                  right: 4,
                  backgroundColor: '#178B8E',
                  color: 'white',
                  width: 34,
                  height: 34,
                  '&:hover': { backgroundColor: '#146f72' },
                }}
              >
                <CameraAltIcon sx={{ fontSize: 18 }} />
              </IconButton>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                hidden
                onChange={handlePhotoChange}
              />
            </>
          )}
        </Box>
        {isUploading && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
            Uploading...
          </Typography>
        )}
      </Box>

      <Divider sx={{ mb: 3 }} />

      <Grid container spacing={2}>
        {/* Name */}
        <Grid size={{ xs: 12, sm: 6 }}>
          {editing ? (
            <TextField
              fullWidth
              label="First Name"
              value={draft.firstName}
              onChange={(e) => handleField('firstName', e.target.value)}
              size="small"
            />
          ) : (
            <Box>
              <Typography variant="caption" color="text.secondary">
                First Name
              </Typography>
              <Typography variant="body2" fontWeight={500}>
                {data.firstName}
              </Typography>
            </Box>
          )}
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          {editing ? (
            <TextField
              fullWidth
              label="Last Name"
              value={draft.lastName}
              onChange={(e) => handleField('lastName', e.target.value)}
              size="small"
            />
          ) : (
            <Box>
              <Typography variant="caption" color="text.secondary">
                Last Name
              </Typography>
              <Typography variant="body2" fontWeight={500}>
                {data.lastName}
              </Typography>
            </Box>
          )}
        </Grid>

        {/* Contact */}
        <Grid size={{ xs: 12, sm: 6 }}>
          {editing ? (
            <TextField
              fullWidth
              label="Email"
              value={draft.email}
              onChange={(e) => handleField('email', e.target.value)}
              size="small"
            />
          ) : (
            <Box>
              <Typography variant="caption" color="text.secondary">
                Email
              </Typography>
              <Typography variant="body2" fontWeight={500}>
                {data.email}
              </Typography>
            </Box>
          )}
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          {editing ? (
            <TextField
              fullWidth
              label="Phone"
              value={draft.phone}
              onChange={(e) => handleField('phone', e.target.value)}
              size="small"
            />
          ) : (
            <Box>
              <Typography variant="caption" color="text.secondary">
                Phone
              </Typography>
              <Typography variant="body2" fontWeight={500}>
                {data.phone}
              </Typography>
            </Box>
          )}
        </Grid>

        {/* Location */}
        <Grid size={{ xs: 12, sm: 5 }}>
          {editing ? (
            <TextField
              fullWidth
              label="City"
              value={draft.location.city}
              onChange={(e) => handleLocation('city', e.target.value)}
              size="small"
            />
          ) : (
            <Box>
              <Typography variant="caption" color="text.secondary">
                City
              </Typography>
              <Typography variant="body2" fontWeight={500}>
                {data.location.city}
              </Typography>
            </Box>
          )}
        </Grid>
        <Grid size={{ xs: 12, sm: 3 }}>
          {editing ? (
            <TextField
              fullWidth
              label="State"
              value={draft.location.state}
              onChange={(e) => handleLocation('state', e.target.value)}
              size="small"
            />
          ) : (
            <Box>
              <Typography variant="caption" color="text.secondary">
                State
              </Typography>
              <Typography variant="body2" fontWeight={500}>
                {data.location.state}
              </Typography>
            </Box>
          )}
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          {editing ? (
            <TextField
              fullWidth
              label="Zip Code"
              value={draft.location.zipCode}
              onChange={(e) => handleLocation('zipCode', e.target.value)}
              size="small"
            />
          ) : (
            <Box>
              <Typography variant="caption" color="text.secondary">
                Zip Code
              </Typography>
              <Typography variant="body2" fontWeight={500}>
                {data.location.zipCode}
              </Typography>
            </Box>
          )}
        </Grid>

        {/* Bio */}
        <Grid size={{ xs: 12 }}>
          {editing ? (
            <Box>
              <TextField
                fullWidth
                label="Bio"
                multiline
                rows={4}
                value={draft.bio}
                onChange={(e) => {
                  if (e.target.value.length <= 500) handleField('bio', e.target.value);
                }}
                size="small"
                helperText={`${draft.bio.length}/500`}
              />
            </Box>
          ) : (
            <Box>
              <Typography variant="caption" color="text.secondary">
                Bio
              </Typography>
              <Typography variant="body2" fontWeight={500}>
                {data.bio}
              </Typography>
            </Box>
          )}
        </Grid>

        {/* Experience */}
        <Grid size={{ xs: 12, sm: 6 }}>
          {editing ? (
            <TextField
              select
              fullWidth
              label="Years of Experience"
              value={draft.experience}
              onChange={(e) => handleField('experience', e.target.value)}
              size="small"
            >
              {EXPERIENCE_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </TextField>
          ) : (
            <Box>
              <Typography variant="caption" color="text.secondary">
                Years of Experience
              </Typography>
              <Typography variant="body2" fontWeight={500}>
                {EXPERIENCE_OPTIONS.find((o) => o.value === data.experience)?.label ??
                  data.experience}
              </Typography>
            </Box>
          )}
        </Grid>

        {/* Languages */}
        <Grid size={{ xs: 12 }}>
          <Typography variant="caption" color="text.secondary">
            Languages
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 0.5 }}>
            {LANGUAGE_OPTIONS.map((lang) => {
              const selected = display.languages.includes(lang);
              return (
                <Chip
                  key={lang}
                  label={lang}
                  size="small"
                  onClick={editing ? () => handleLanguageToggle(lang) : undefined}
                  sx={{
                    cursor: editing ? 'pointer' : 'default',
                    backgroundColor: selected ? 'rgba(23,139,142,0.12)' : '#F5F5F5',
                    color: selected ? '#178B8E' : 'text.secondary',
                    fontWeight: selected ? 600 : 400,
                    border: selected ? '1px solid rgba(23,139,142,0.4)' : '1px solid transparent',
                  }}
                />
              );
            })}
          </Box>
        </Grid>
      </Grid>

      {editing && (
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', mt: 3 }}>
          <Button
            variant="outlined"
            size="small"
            onClick={handleCancel}
            sx={{ textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            size="small"
            onClick={handleSave}
            sx={{
              textTransform: 'none',
              backgroundColor: '#178B8E',
              '&:hover': { backgroundColor: '#146f72' },
            }}
          >
            Save
          </Button>
        </Box>
      )}
    </Paper>
  );
};

export default ProfileSection;
